import { UserRole } from './user'
import type { Option } from './option'

export enum InventoryItemKind {
    consumable = 'consumable',
    medication = 'medication',
}

export enum StockMovementReason {
    restock = 'restock',
    use = 'use',
    useReversal = 'use_reversal',
    adjust = 'adjust',
    returnHdf = 'return_hdf',
    transferOut = 'transfer_out',
    transferIn = 'transfer_in',
    expired = 'expired',
}

export enum EquipmentCondition {
    ok = 'ok',
    needsAttention = 'needs_attention',
    outOfService = 'out_of_service',
}

export enum InterventionStatus {
    inProgress = 'in_progress',
    completed = 'completed',
    refusedCare = 'refused_care',
}

export enum TreatmentStatus {
    prepared = 'prepared',
    administered = 'administered',
    cancelled = 'cancelled',
}

export enum MedicationRoute {
    oral = 'oral',
    iv = 'iv',
    im = 'im',
    sc = 'sc',
    inhaled = 'inhaled',
    topical = 'topical',
    sublingual = 'sublingual',
    rectal = 'rectal',
    other = 'other',
}

export const FIELD_POST_READ_ROLES = [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator, UserRole.fieldMedic]
export const FIELD_POST_WRITE_ROLES = [UserRole.admin, UserRole.medicalDirector, UserRole.fieldMedic]
export const FIELD_POST_MANAGE_ROLES = [UserRole.admin, UserRole.medicalDirector]

export interface FieldPostStorage {
    id: string
    post_id: string
    name: string
    sort_order: number
    is_archived: boolean
}

export interface FieldPost {
    id: string
    name: string
    code: string
    areas: string[]
    is_active: boolean
    storages?: FieldPostStorage[]
}

export interface InventoryItem {
    id: string
    kind: InventoryItemKind
    name: string
    specification: string | null
    unit: string
    is_archived: boolean
}

export interface StockLot {
    id: string
    post_id: string
    storage_id: string
    item_id: string
    lot_number: string | null
    expires_on: string | null
    qty_on_hand: number
    item?: InventoryItem
    storage?: FieldPostStorage
}

export interface StockMovement {
    id: string
    lot_id: string
    delta: number
    reason: StockMovementReason
    note: string | null
    transfer_group: string | null
    intervention_usage_id: string | null
    created_at: string
    creator?: { id: string; name: string } | null
    lot?: { lot_number: string | null; item?: { name: string; specification: string | null } }
}

export interface FieldPostEquipment {
    id: string
    post_id: string
    storage_id: string | null
    name: string
    specification: string | null
    qty: number
    condition: EquipmentCondition
    situation: string | null
    action: string | null
}

export type VitalKey = 'bp_systolic' | 'bp_diastolic' | 'pulse' | 'spo2' | 'respiratory_rate' | 'gcs' | 'pain' | 'glucose' | 'temperature'
export type Vitals = Partial<Record<VitalKey, number>>

export interface VitalField {
    key: VitalKey
    label: string
    unit: string
    min: number
    max: number
}

export const VITAL_FIELDS: VitalField[] = [
    { key: 'bp_systolic', label: 'BP systolic', unit: 'mmHg', min: 0, max: 300 },
    { key: 'bp_diastolic', label: 'BP diastolic', unit: 'mmHg', min: 0, max: 200 },
    { key: 'pulse', label: 'Pulse', unit: 'bpm', min: 0, max: 300 },
    { key: 'spo2', label: 'SpO₂', unit: '%', min: 0, max: 100 },
    { key: 'respiratory_rate', label: 'Resp. rate', unit: '/min', min: 0, max: 80 },
    { key: 'gcs', label: 'GCS', unit: '3–15', min: 3, max: 15 },
    { key: 'pain', label: 'Pain', unit: '0–10', min: 0, max: 10 },
    { key: 'glucose', label: 'Glucose', unit: 'mg/dL', min: 0, max: 2000 },
    { key: 'temperature', label: 'Temperature', unit: '°C', min: 25, max: 45 },
]

export const POST_VITAL_KEYS: VitalKey[] = ['bp_systolic', 'bp_diastolic', 'pulse', 'spo2', 'respiratory_rate', 'gcs', 'temperature']

export interface Intervention {
    id: string
    number: number
    post_id: string
    area: string | null
    customer_id: string
    status: InterventionStatus
    arrived_at: string
    discharged_at: string | null
    has_allergy: boolean | null
    allergy_details: string | null
    reason_id: string | null
    circumstances: string | null
    vitals_initial: Vitals
    vitals_post: Vitals
    clinical_exam: string | null
    precautions: string | null
    disposition_id: string | null
    progress_notes: string | null
    emergency_contact_name: string | null
    emergency_contact_phone: string | null
    nurse_id: string | null
    physician_id: string | null
    created_by: string
    updated_at: string
    reason?: Option | null
    disposition?: Option | null
    post?: Pick<FieldPost, 'id' | 'name' | 'areas'>
}

export interface FieldPostPatient {
    intervention_id?: string
    id: string
    first_name: string | null
    last_name: string | null
    birth_date: string | null
    gender: 'male' | 'female' | null
    blood_type: string | null
    phone: string | null
    allergies: string | null
    source: 'app' | 'portal' | 'field_post' | null
}

export interface TeamMember {
    id: string
    name: string
}

export interface InterventionTreatment {
    id: string
    intervention_id: string
    given_at: string
    treatment_id: string
    physician_id: string | null
    nurse_id: string | null
    status: TreatmentStatus
    notes: string | null
}

export interface InterventionUsage {
    id: string
    intervention_id: string
    lot_id: string
    used_at: string
    qty: number
    used_by: string | null
    notes: string | null
    dose: string | null
    route: MedicationRoute | null
    status: TreatmentStatus | null
    lot?: StockLot
}

export interface InterventionReport {
    id: string
    intervention_id: string
    type: 'feedback'
    notes: string
    user_id: string
    created_at: string
    user?: { id: string; name: string } | null
}

export type StockActionMode = 'restock' | 'adjust' | 'transfer' | 'return_hdf' | 'expired'

export const GENDER_OPTIONS = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
]

export const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown']

export const ROUTE_OPTIONS = Object.values(MedicationRoute).map(value => ({
    value,
    label: ['iv', 'im', 'sc'].includes(value) ? value.toUpperCase() : value.charAt(0).toUpperCase() + value.slice(1),
}))

export const TREATMENT_STATUS_OPTIONS = [
    { value: TreatmentStatus.prepared, label: 'Prepared' },
    { value: TreatmentStatus.administered, label: 'Administered' },
    { value: TreatmentStatus.cancelled, label: 'Cancelled' },
]

export const LOT_STATUS_META = {
    negative: { label: 'Count Mismatch', color: 'red' },
    expired: { label: 'Expired', color: 'red' },
    expiring: { label: 'Expiring Soon', color: 'orange' },
    ok: { label: 'OK', color: 'emerald' },
} as const

export const CONDITION_META = {
    [EquipmentCondition.ok]: { label: 'OK', color: 'emerald' },
    [EquipmentCondition.needsAttention]: { label: 'Needs Attention', color: 'orange' },
    [EquipmentCondition.outOfService]: { label: 'Out of Service', color: 'red' },
} as const

export const INTERVENTION_STATUS_META = {
    [InterventionStatus.inProgress]: { label: 'In Progress', color: 'orange' },
    [InterventionStatus.completed]: { label: 'Completed', color: 'emerald' },
    [InterventionStatus.refusedCare]: { label: 'Refused Care', color: 'gray' },
} as const

export const MOVEMENT_REASON_LABELS: Record<StockMovementReason, string> = {
    [StockMovementReason.restock]: 'Restock',
    [StockMovementReason.use]: 'Used',
    [StockMovementReason.useReversal]: 'Usage Reversed',
    [StockMovementReason.adjust]: 'Count Adjusted',
    [StockMovementReason.returnHdf]: 'Returned to HDF',
    [StockMovementReason.transferOut]: 'Transferred Out',
    [StockMovementReason.transferIn]: 'Transferred In',
    [StockMovementReason.expired]: 'Expired',
}

export const MISSING_LABELS: Record<string, string> = {
    reason_id: 'Reason',
    disposition_id: 'Disposition',
    discharged_at: 'Discharge Time',
    team: 'Nurse or Physician',
}
